import { userService } from './UserService';
import { VehicleData } from '@/types';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, AxiosHeaders } from 'axios';
import { URI } from '@/types/enum/api.enum';

class ApiInterceptor {
  private static instance: ApiInterceptor;
  private readonly baseUrl: string;
  private readonly axiosInstance: AxiosInstance;
  private vehicleData: VehicleData | null = null;
  private isRefreshingToken = false;
  private retryCount = 0;
  private readonly MAX_RETRIES = 1;
  private currentPageRoute: string = 'home';

  private constructor() {
    this.baseUrl = '';
    this.axiosInstance = this.createAxiosInstance();
    this.setupInterceptors();
  }

  private createAxiosInstance(): AxiosInstance {
    return axios.create({
      baseURL: this.baseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  private setupInterceptors(): void {
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  private setupRequestInterceptor(): void {
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        if (!config) return config;

        // Add page route prefix to API calls
        if (config.url && config.url.startsWith('/api')) {
          const originalUrl = config.url;
          config.url = `/${this.currentPageRoute}${config.url}`;
        }

        // Skip auth for login endpoint (check both original and prefixed URLs)
        if (this.isLoginEndpoint(config.url)) {
          return config;
        }

        // Ensure headers exist
        if (!config.headers) {
          config.headers = new AxiosHeaders();
        }

        // Get current token
        let token = userService.getToken();

        // If no token, try to get one
        if (!token) {
          try {
            await this.autoLogin();
            token = userService.getToken();
          } catch (error) {
            return Promise.reject(error);
          }
        }

        // Set token in headers
        if (token) {
          config.headers['Authorization'] = token;
        }

        // Add vehicle data if available
        this.addVehicleData(config);

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  private setupResponseInterceptor(): void {
    this.axiosInstance.interceptors.response.use(
      (response) => {
        this.retryCount = 0;
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config;

        // Skip handling for login endpoint (check both original and prefixed URLs)
        if (this.isLoginEndpoint(originalRequest?.url)) {
          return Promise.reject(error);
        }

        // Handle 401 errors
        if (error.response?.status === 401) {
          return this.handleUnauthorizedError(originalRequest, error);
        }

        return Promise.reject(error);
      }
    );
  }

  private async handleUnauthorizedError(originalRequest: AxiosRequestConfig | undefined, error: AxiosError): Promise<AxiosResponse | Promise<never>> {
    // If already refreshing or exceeded retries, logout and reject
    if (this.isRefreshingToken || this.retryCount >= this.MAX_RETRIES) {
      userService.logout();
      return Promise.reject(error);
    }

    this.isRefreshingToken = true;
    this.retryCount++;

    try {
      await this.autoLogin();
      const newToken = userService.getToken();

      if (newToken && originalRequest) {
        if (!originalRequest.headers) {
          originalRequest.headers = new AxiosHeaders();
        }
        originalRequest.headers['Authorization'] = newToken;
        return this.axiosInstance(originalRequest);
      }
    } catch (refreshError) {
      userService.logout();
      return Promise.reject(refreshError);
    } finally {
      this.isRefreshingToken = false;
    }

    return Promise.reject(error);
  }

  private addVehicleData(config: AxiosRequestConfig): void {
    if (!this.vehicleData) return;

    const vehicleDetails = {
      vinDetails: {
        year: this.vehicleData.year,
        make: this.vehicleData.make,
        model: this.vehicleData.model,
        raw: this.vehicleData.raw
      }
    };

    if (config.data) {
      config.data = { ...config.data, ...vehicleDetails };
    } else {
      config.data = vehicleDetails;
    }
  }

  private isLoginEndpoint(url: string | undefined): boolean {
    if (!url) return false;
    return url === URI.LOGIN || url === `/${this.currentPageRoute}${URI.LOGIN}`;
  }

  private async autoLogin(): Promise<void> {
    const credentials = {
      email: 'guest_user@predii.com',
      password: 'Predii123'
    };
    
    await userService.login(credentials.email, credentials.password);
  }

  public static getInstance(): ApiInterceptor {
    if (!ApiInterceptor.instance) {
      ApiInterceptor.instance = new ApiInterceptor();
    }
    return ApiInterceptor.instance;
  }

  public setVehicleData(data: VehicleData | null): void {
    this.vehicleData = data;
  }

  public setPageRoute(pageRoute: string): void {
    this.currentPageRoute = pageRoute;
  }

  public getPageRoute(): string {
    return this.currentPageRoute;
  }

  public getApiBaseUrl(): string {
    return `/${this.currentPageRoute}/api`;
  }

  public async get<T>(endpoint: string, config?: AxiosRequestConfig, signal?: AbortSignal): Promise<T> {
    try {
      const mergedConfig = { ...config, signal };
      const response = await this.axiosInstance.get<T>(endpoint, mergedConfig);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  public async post<T, D extends Record<string, unknown>>(
    endpoint: string,
    data: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.post<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  public async put<T, D extends Record<string, unknown>>(
    endpoint: string,
    data: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.put<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  public async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.axiosInstance.delete<T>(endpoint, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  private handleError(error: unknown): void {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        // Error handling logic preserved but without console.error
      } else if (axiosError.request) {
        // Error handling logic preserved but without console.error
      } else {
        // Error handling logic preserved but without console.error
      }
    }
  }
}

export const apiInterceptor = ApiInterceptor.getInstance(); 